import React, { useState, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiUsers } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { AddNationality, EditNationality } from '../components/nationalities';
import { useNationalities, useDeleteNationality } from '../hooks/useNationalities';

const Nationalities = ({ user }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedNationality, setSelectedNationality] = useState(null);

  // Fetch nationalities using React Query
  const {
    data: nationalitiesResponse,
    isLoading,
    error,
    isFetching,
  } = useNationalities(pagination.current, pagination.pageSize);

  // Mutations
  const deleteNationalityMutation = useDeleteNationality();

  // Memoized nationalities data
  const { nationalitiesData, totalRecords } = useMemo(() => {
    if (!nationalitiesResponse) {
      return { nationalitiesData: [], totalRecords: 0 };
    }

    // Ensure we always return an array for nationalitiesData
    const nationalities = nationalitiesResponse.nationalities.data || nationalitiesResponse.data || [];
    const total = nationalitiesResponse.nationalities.total || nationalitiesResponse.count || 0;

    return {
      nationalitiesData: Array.isArray(nationalities) ? nationalities : [],
      totalRecords: total
    };
  }, [nationalitiesResponse]);

  // Check if delete mutation is loading
  const isMutating = deleteNationalityMutation.isPending;

  // Get error message from mutations or query
  const errorMessage = error?.message || 
                      deleteNationalityMutation.error?.message;

  // Table columns configuration
  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true,
    },
    {
      key: 'nationality_en',
      title: 'Nationality',
      sortable: true,
      render: (value, row) => (
        <span className="text-gray-900 font-medium">
          {row.nationality?.en || 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEditNationality(row)}
            className="text-green-600 hover:text-green-800 p-1"
            title="Edit Nationality"
            disabled={isMutating}
          >
            <FiEdit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteNationality(row)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Delete Nationality"
            disabled={isMutating}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  // Breadcrumbs
  const breadcrumbs = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Nationalities', href: '/dashboard/nationalities' },
  ];

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handlePageSizeChange = (pageSize, current) => {
    setPagination(prev => ({ ...prev, pageSize, current }));
  };

  const handleAddNationality = () => {
    setSelectedNationality(null);
    setShowAddModal(true);
  };

  const handleEditNationality = (nationality) => {
    setSelectedNationality(nationality);
    setShowEditModal(true);
  };

  const handleDeleteNationality = async (nationality) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete nationality "${nationality.nationality?.en || 'N/A'}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      color: '#333',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
        cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
      }
    });

    if (result.isConfirmed) {
      try {
        await deleteNationalityMutation.mutateAsync(nationality.id);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Nationality has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          background: '#ffffff',
          color: '#333',
          customClass: {
            popup: 'rounded-lg shadow-xl',
          }
        });
      } catch (error) {
        console.error('Error deleting nationality:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete nationality. Please try again.',
          confirmButtonText: 'OK',
          background: '#ffffff',
          color: '#333',
          customClass: {
            popup: 'rounded-lg shadow-xl',
          }
        });
      }
    }
  };

  return (
    <Layout>
      <div className="">
        <PageHeader
          title="Nationalities Management"
          breadcrumbs={breadcrumbs}
          action={{
            label: 'Add Nationality',
            onClick: handleAddNationality,
            icon: <FiPlus className="h-4 w-4" />,
          }}
        />

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{errorMessage}</p>
          </div>
        )}

        {/* Add Nationality Button */}
        <div className="flex justify-end !mb-4">
          <button
            onClick={handleAddNationality}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            disabled={isMutating}
          >
            <FiPlus className="h-4 w-4 mr-2" />
            Add Nationality
          </button>
        </div>

        {/* Nationalities Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <DataTable
            data={nationalitiesData}
            columns={columns}
            filterable={false}
            loading={isLoading || isFetching}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalRecords,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} nationalities`,
            }}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            emptyText="No nationalities found"
            emptyIcon={<FiUsers className="h-12 w-12 text-gray-400" />}
          />
        </div>

        {/* Add Nationality Modal */}
        {showAddModal && (
          <AddNationality
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
          />
        )}

        {/* Edit Nationality Modal */}
        {showEditModal && (
          <EditNationality
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            nationality={selectedNationality}
          />
        )}
      </div>
    </Layout>
  );
};

export default Nationalities; 